import { z } from "zod";
import { responseSchema } from "../http.js";
/**
 * ═══════════════════════════════════════════════════════════
 * Contenido del sitio: banner, documentos legales y recursos
 *
 * Réplica de `admin/dashboard-banner`, `admin/legal-docs` y
 * `admin/v2/resources` (Anahí, 27-ago). Tres cosas pequeñas con la
 * misma forma: el panel las EDITA y el sitio las LEE.
 * ═══════════════════════════════════════════════════════════
 */
// ── El banner del sitio ──────────────────────────────────
/**
 * La franja de anuncio sobre la tienda. UN banner, no una lista: es un
 * megáfono, y dos megáfonos a la vez es ninguno. Apagado, la tienda ni
 * lo pinta.
 */
export const bannerSettingsInputSchema = z.object({
    enabled: z.boolean(),
    title: z.string().trim().max(80),
    message: z.string().trim().max(200).optional(),
    ctaLabel: z.string().trim().max(40).optional(),
    ctaTo: z.string().trim().max(200).optional(),
});
export const bannerSettingsSchema = bannerSettingsInputSchema.extend({
    updatedAt: z.iso.datetime(),
});
export const bannerResponseSchema = responseSchema(z.object({ banner: bannerSettingsSchema }));
export const BANNER_ROUTES = {
    /** Público: la tienda lo pide al pintar. */
    public: "/api/banner",
    admin: "/api/admin/banner",
};
// ── El banner del PANEL (dashboards de socio y franquiciatario) ───
/**
 * La imagen grande que corona los dashboards, como en el sistema viejo
 * ("El capítulo más importante está por comenzar"). Es OTRO banner: el
 * de la tienda es una franja de texto; este es una imagen de campaña.
 * La imagen viaja como dataUrl (subida desde el panel) o como URL.
 */
export const panelBannerInputSchema = z.object({
    enabled: z.boolean(),
    /** dataUrl de la imagen subida, o una URL https. Vacío = sin banner. */
    imageUrl: z.string().trim().max(6_000_000),
    /** Lo que "dice" la imagen, para quien no la ve. */
    alt: z.string().trim().max(160).default(""),
    /** A dónde lleva el clic, si lleva a algún lado ("/paquetes"). */
    linkTo: z.string().trim().max(200).default(""),
});
export const panelBannerSchema = panelBannerInputSchema.extend({
    updatedAt: z.iso.datetime(),
});
export const panelBannerResponseSchema = responseSchema(z.object({ banner: panelBannerSchema }));
export const PANEL_BANNER_ROUTES = {
    /** Con sesión: los dashboards lo piden al pintar. */
    mine: "/api/banner/panel",
    admin: "/api/admin/banner/panel",
};
// ── Los documentos legales ───────────────────────────────
/** Los cuatro que promete el pie del sitio. */
export const LEGAL_DOC_SLUGS = ["privacidad", "terminos", "envios", "devoluciones"];
export const legalDocSlugSchema = z.enum(LEGAL_DOC_SLUGS);
export const LEGAL_DOC_LABELS = {
    privacidad: "Aviso de privacidad",
    terminos: "Términos y condiciones",
    envios: "Política de envío",
    devoluciones: "Devoluciones",
};
export const legalDocInputSchema = z.object({
    title: z.string().trim().min(1, "Escribe el título").max(120),
    /** Texto plano con párrafos: el sitio lo pinta con sus saltos. */
    content: z.string().trim().min(1, "El documento no puede quedar vacío").max(30_000),
});
export const legalDocSchema = legalDocInputSchema.extend({
    slug: legalDocSlugSchema,
    updatedAt: z.iso.datetime(),
});
export const legalDocListResponseSchema = responseSchema(z.object({ items: z.array(legalDocSchema) }));
export const legalDocResponseSchema = responseSchema(z.object({ doc: legalDocSchema }));
export const LEGAL_ROUTES = {
    /** Público: `/legal` los pinta. Solo los que existen. */
    public: "/api/legal-docs",
    adminList: "/api/admin/legal-docs",
    adminOne: (slug) => `/api/admin/legal-docs/${slug}`,
};
// ── Los recursos ─────────────────────────────────────────
/** La biblioteca para socios: videos, PDFs, imágenes y enlaces. */
export const RESOURCE_KINDS = ["video", "pdf", "imagen", "link"];
export const resourceKindSchema = z.enum(RESOURCE_KINDS);
export const RESOURCE_KIND_LABELS = {
    video: "Video",
    pdf: "PDF",
    imagen: "Imagen",
    link: "Enlace",
};
/**
 * ── De dónde sale el material de un recurso ──────────────────────────
 *
 * CADA tipo tiene UNA sola fuente, y por eso el formulario puede pedir
 * solo lo que corresponde (Anahí, 28-ago: "hoy enseña campos que no
 * aplican al tipo elegido"):
 *
 *  · `pdf` e `imagen` → ARCHIVO subido. Vive en Mongo como `Buffer`,
 *    igual que el KYC y el comprobante de pago: es el único almacén de
 *    binarios que este sistema tiene, y un tríptico de 3 MB cabe de
 *    sobra. Pedirle al admin que suba el PDF a otro lado y pegue la URL
 *    era pedirle que resolviera a mano lo que el panel debe hacer.
 *
 *  · `video` y `link` → ENLACE. Un video NO se sube: los del sistema
 *    viejo pesan decenas o cientos de MB, y meter eso en un documento
 *    de Mongo (techo de 16 MB, y el documento entero viaja en cada
 *    lectura sin proyección) es una mala idea con nombre propio. Van
 *    por YouTube/Vimeo/Drive, que además dan reproducción por tramos.
 *    La interfaz lo DICE en una línea en vez de esconder el botón.
 *
 * El día que exista un almacén de objetos, `pdf`/`imagen` cambian de
 * transporte aquí y `video` gana la opción de subida. Nada más.
 */
/** Lo que se acepta como archivo. Lista cerrada: un .exe no es un recurso. */
export const RESOURCE_FILE_MIME_TYPES = [
    "application/pdf",
    "image/jpeg",
    "image/png",
    "image/webp",
];
export const resourceFileMimeSchema = z.enum(RESOURCE_FILE_MIME_TYPES);
/**
 * Techo del ARCHIVO (no del base64). 8 MB y no los 4 del comprobante
 * porque los trípticos del sistema viejo llegan a 5 MB: un techo que
 * rebota el material que hay que importar no es un techo, es un bug.
 */
export const RESOURCE_FILE_MAX_BYTES = 8 * 1024 * 1024;
/**
 * Techo de la PORTADA, en caracteres del `data:` (≈ 225 KB de imagen).
 * Es una miniatura que viaja en cada fila de la lista: el techo mide lo
 * que la rejilla puede pintar de golpe, no lo que una foto pesa.
 */
export const RESOURCE_COVER_MAX_CHARS = 300_000;
/** El lado mayor al que se reduce una portada antes de subirla. */
export const RESOURCE_COVER_MAX_SIDE = 720;
/** Qué formato acepta cada tipo. Vacío = ese tipo no lleva archivo. */
export const RESOURCE_KIND_MIME_TYPES = {
    video: [],
    pdf: ["application/pdf"],
    imagen: ["image/jpeg", "image/png", "image/webp"],
    link: [],
};
/** Los tipos que se SUBEN (los otros dos se enlazan). */
export const UPLOADABLE_RESOURCE_KINDS = ["pdf", "imagen"];
export const isUploadableResourceKind = (kind) => RESOURCE_KIND_MIME_TYPES[kind].length > 0;
/** El archivo al SUBIR: base64 en el JSON, el mismo sobre que el KYC. */
export const resourceFileInputSchema = z.object({
    fileName: z.string().trim().min(1, "Falta el nombre del archivo").max(200),
    mimeType: resourceFileMimeSchema,
    dataBase64: z
        .string()
        .min(1, "El archivo llegó vacío")
        .max(Math.ceil(RESOURCE_FILE_MAX_BYTES / 3) * 4 + 8, "El archivo no puede pasar de 8 MB"),
});
/**
 * El archivo al SALIR: solo su ficha. `dataUrl` viaja ÚNICAMENTE cuando
 * alguien pide ese recurso por su ruta de archivo — en la lista serían
 * decenas de megabytes por pantalla que nadie mira.
 */
export const resourceFileSchema = z.object({
    fileName: z.string(),
    mimeType: resourceFileMimeSchema,
    sizeBytes: z.number().int().min(0),
    dataUrl: z.string().optional(),
});
const resourceBaseSchema = z.object({
    title: z.string().trim().min(2, "Escribe el título").max(100),
    description: z.string().trim().max(300).optional(),
    kind: resourceKindSchema,
    /** Solo para `video` y `link`: a dónde lleva. */
    url: z.string().trim().url("Escribe la URL completa, con https://").max(500).optional(),
    /** Solo para `pdf` e `imagen`: el archivo que se sube. */
    file: resourceFileInputSchema.optional(),
    /**
     * El grupo de la biblioteca ("Trípticos", "Formación"…): la
     * biblioteca del socio agrupa por esto, con su conteo, como el
     * sistema anterior. Texto libre — las categorías nacen al usarlas.
     */
    category: z.string().trim().max(40).optional(),
    /**
     * La portada de la card. Sin ella, la biblioteca pinta su placeholder.
     *
     * Admite una URL o un `data:` de imagen SUBIDA (Anahí, 28-ago: "esto
     * quítalo, y si es una imagen se debe poder subir una imagen"). Va
     * inline y no como `Buffer` aparte porque la portada viaja en la LISTA
     * —es lo que se pinta al abrir la biblioteca— y una lista que tuviera
     * que pedir veinte archivos para dibujarse no es una lista. Por eso el
     * techo es de MINIATURA, no de archivo: quien sube la portada la
     * reduce antes (ver `resources.api.ts`); un PNG de 5 MB por card
     * multiplicado por la rejilla es una pantalla que no carga.
     */
    imageUrl: z
        .string()
        .trim()
        .max(RESOURCE_COVER_MAX_CHARS, "La portada pesa demasiado: usa una imagen más chica")
        .refine((value) => /^https?:\/\//.test(value) || value.startsWith("data:image/"), "La portada es una URL completa o una imagen subida")
        .optional(),
    active: z.boolean().default(true),
});
/** El archivo tiene que ser del formato que ese tipo admite. */
function checkFormato(value, ctx) {
    if (!value.kind || !value.file)
        return;
    const allowed = RESOURCE_KIND_MIME_TYPES[value.kind];
    if (!allowed.includes(value.file.mimeType)) {
        ctx.addIssue({
            code: "custom",
            path: ["file"],
            message: allowed.length === 0
                ? `Un recurso de tipo ${RESOURCE_KIND_LABELS[value.kind].toLowerCase()} va por enlace, no por archivo`
                : `Ese formato no corresponde a un ${RESOURCE_KIND_LABELS[value.kind]}`,
        });
    }
}
/** Al crear: cada tipo EXIGE su fuente, y solo la suya. */
export const resourceInputSchema = resourceBaseSchema.superRefine((value, ctx) => {
    checkFormato(value, ctx);
    if (isUploadableResourceKind(value.kind)) {
        if (!value.file) {
            ctx.addIssue({
                code: "custom",
                path: ["file"],
                message: value.kind === "pdf" ? "Sube el PDF" : "Sube la imagen",
            });
        }
        if (value.url) {
            ctx.addIssue({
                code: "custom",
                path: ["url"],
                message: "Este tipo se sube como archivo: no lleva enlace",
            });
        }
        return;
    }
    if (!value.url) {
        ctx.addIssue({
            code: "custom",
            path: ["url"],
            message: value.kind === "video" ? "Pega el enlace del video" : "Pega el enlace",
        });
    }
});
/**
 * Al editar: todo opcional (apagar un recurso manda solo `active`), pero
 * si viene archivo tiene que cuadrar con el tipo que venga con él.
 */
export const resourceUpdateSchema = resourceBaseSchema.partial().superRefine(checkFormato);
/**
 * Cómo SALE un recurso. Ni `url` ni `file` son obligatorios aquí: la
 * validación de "cada tipo con su fuente" es del alta, y este esquema
 * también tiene que poder pintar lo que ya estaba guardado antes.
 */
export const resourceSchema = resourceBaseSchema.omit({ file: true }).extend({
    id: z.string(),
    createdAt: z.iso.datetime(),
    file: resourceFileSchema.optional(),
});
/** El panel filtra y pagina en el servidor; la BIBLIOTECA del socio
 * sigue pidiendo la lista activa entera — ella agrupa por categoría. */
export const resourceAdminQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(100).default(24),
    query: z.string().trim().max(100).optional(),
    kind: resourceKindSchema.optional(),
    status: z.enum(["active", "inactive"]).optional(),
});
export const resourceAdminListResponseSchema = responseSchema(z.object({
    items: z.array(resourceSchema),
    total: z.number().int().min(0),
    page: z.number().int().min(1),
    perPage: z.number().int().min(1),
}));
export const resourceListResponseSchema = responseSchema(z.object({ items: z.array(resourceSchema) }));
export const resourceResponseSchema = responseSchema(z.object({ resource: resourceSchema }));
export const RESOURCE_ROUTES = {
    /** Con sesión: la biblioteca es para la comunidad, no para la calle. */
    list: "/api/resources",
    /**
     * El recurso CON su archivo dentro (`file.dataUrl`). Ruta aparte —y no
     * un campo más de la lista— porque son megabytes: se piden en el clic,
     * uno a uno, no veinte al pintar la biblioteca.
     */
    file: (id) => `/api/resources/${id}/archivo`,
    adminList: "/api/admin/resources",
    adminCreate: "/api/admin/resources",
    /** Editar (PATCH) y ELIMINAR (DELETE) van por la misma ruta. */
    adminOne: (id) => `/api/admin/resources/${id}`,
};
/** Misma forma que el borrado de un producto: se confirma, no se calla. */
export const resourceDeletedResponseSchema = responseSchema(z.object({ deleted: z.literal(true) }));
// ═══════════════════════════════════════════════════════════
// La cuenta a la que se transfiere (Anahí, 29-ago)
// ═══════════════════════════════════════════════════════════
/**
 * ═══════════════════════════════════════════════════════════════════
 * LA CUENTA DE LA CASA.
 *
 * "Si eligen transferencia, muestra el número de cuenta a depositar."
 *
 * Ya existía la cuenta del FRANQUICIATARIO —a la que transfiere quien
 * compra en su tienda— pero no la de It's The Best, así que un pedido
 * normal con transferencia terminaba en un "conserva tu comprobante" sin
 * decir a dónde mandar el dinero. Pedir una transferencia sin dar la
 * cuenta es pedirle a la clienta que escriba por WhatsApp para poder
 * pagar, y ahí es donde se caen las ventas.
 *
 * ── Por qué es un AJUSTE y no una constante ──────────────────────────
 * Una CLABE cambia: se abre otra cuenta, se cambia de banco, se corrige
 * un dígito. Escrita en el código, cambiarla exige un despliegue; como
 * ajuste, se edita en el panel y surte efecto en el pedido siguiente.
 *
 * ── Puede NO estar configurada ───────────────────────────────────────
 * `null` mientras nadie la capture. La pantalla lo dice con todas sus
 * letras en vez de enseñar una tarjeta vacía — mismo trato que Stripe y
 * Trasso cuando les falta su llave.
 * ═══════════════════════════════════════════════════════════════════
 */
export const bankAccountSchema = z.object({
    bankName: z.string().trim().min(2, "Escribe el banco").max(60),
    /** 18 dígitos exactos: el estándar mexicano. */
    clabe: z.string().trim().regex(/^\d{18}$/, "La CLABE son 18 dígitos"),
    holderName: z.string().trim().min(2, "Escribe el nombre del titular").max(120),
    /** Instrucciones extra: referencia, horarios, a quién avisar. */
    note: z.string().trim().max(300).optional(),
});
/**
 * El número de ruta ABA (EE. UU.): 9 dígitos y un dígito verificador con
 * pesos 3-7-1. Un dígito cambiado manda el ACH a otro banco o lo rebota
 * días después; aquí rebota al capturarlo.
 */
export function isAbaRouting(value) {
    if (!/^\d{9}$/.test(value))
        return false;
    const d = [...value].map(Number);
    const sum = 3 * (d[0] + d[3] + d[6]) + 7 * (d[1] + d[4] + d[7]) + (d[2] + d[5] + d[8]);
    return sum % 10 === 0;
}
/**
 * ═══════════════════════════════════════════════════════════════════
 * LA CUENTA DE LA CASA EN DÓLARES (Anahí, 29-sep-2026).
 *
 * «No pueden generar transferencias»: quien compra desde EE. UU. paga en
 * USD y la única cuenta capturada era una CLABE en pesos. El sistema
 * viejo tenía las dos —Bank of America por ACH para EE. UU., BBVA para
 * el resto— y elegía por país; aquí el país ya decide la moneda del
 * pedido, así que se elige por MONEDA: USD → esta, MXN → la CLABE.
 * ═══════════════════════════════════════════════════════════════════
 */
export const usBankAccountSchema = z.object({
    bankName: z.string().trim().min(2, "Escribe el banco").max(60),
    holderName: z.string().trim().min(2, "Escribe el nombre del titular").max(120),
    accountNumber: z.string().trim().regex(/^\d{4,17}$/, "El número de cuenta son de 4 a 17 dígitos"),
    routingNumber: z.string().trim().refine(isAbaRouting, "El routing number no es válido (9 dígitos)"),
    /** La dirección del titular: algunos bancos la piden para el ACH. */
    address: z.string().trim().max(200).optional(),
    note: z.string().trim().max(300).optional(),
});
/**
 * A dónde deposita quien eligió transferencia: una CLABE (México, la de
 * la casa o la del franquiciatario) o una cuenta ACH (EE. UU.). Sin
 * `type`, es CLABE — así llega la del franquiciatario desde su perfil.
 */
export const depositAccountSchema = z.union([
    usBankAccountSchema.extend({ type: z.literal("us_ach") }),
    bankAccountSchema.extend({ type: z.literal("clabe").optional() }),
]);
export function isUsDepositAccount(account) {
    return account.type === "us_ach";
}
export const bankAccountResponseSchema = responseSchema(z.object({
    /** MXN · CLABE. */
    bankAccount: bankAccountSchema.nullable(),
    /** USD · ACH. Opcional en la respuesta para no romper a quien no la conoce. */
    usBankAccount: usBankAccountSchema.nullable().optional(),
}));
export const BANK_ACCOUNT_ROUTES = {
    /** La lee cualquiera CON SESIÓN: es a dónde transferir su pedido. */
    mine: "/api/payments/bank-account",
    /** La edita el super-admin: la CLABE (MXN). */
    admin: "/api/admin/payments/bank-account",
    /** La edita el super-admin: la cuenta de EE. UU. (USD). */
    adminUsd: "/api/admin/payments/bank-account/usd",
};
//# sourceMappingURL=content.js.map